import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Printer, ArrowLeft, FileText } from 'lucide-react';
import { code128Bars } from '@/utils/barcode128';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }) : '');
const clean = (v) => String(v ?? '').trim();
const cap = 'text-[9px] font-black uppercase tracking-widest';
const box = 'border-b-2 border-black px-3 py-2 shrink-0';

// Scannable Code 128 barcode. Bars use a whole number of pixels so they stay sharp on screen and on thermal printers.
function Barcode({ value, maxWidth, height, testId }) {
  const enc = code128Bars(value);
  if (!enc) return null;
  const quiet = 10;
  const modules = enc.width + quiet * 2;
  const unit = Math.max(1, Math.min(3, Math.floor(maxWidth / modules)));
  const width = modules * unit <= maxWidth ? modules * unit : '100%';
  return (
    <svg
      width={width} height={height} viewBox={`0 0 ${modules} ${height}`} preserveAspectRatio='none'
      shapeRendering='crispEdges' className='block mx-auto' data-testid={testId} aria-label={value}
    >
      <rect width={modules} height={height} fill='#fff' />
      {enc.bars.map(([x, w]) => <rect key={x} x={x + quiet} y={0} width={w} height={height} fill='#000' />)}
    </svg>
  );
}

// One label per tracking (AWB) number, so items shipped in separate parcels each get their own label.
function packagesOf(order) {
  const all = order.items || [];
  const active = all.filter((it) => it.item_status !== 'cancelled');
  const items = active.length ? active : all;
  const fb = order.tracking || {};
  const fbAwb = clean(fb.awb);
  const groups = new Map();
  items.forEach((it) => {
    const awb = clean(it.item_tracking_number) || fbAwb;
    const courier = clean(it.item_courier) || (awb && awb === fbAwb ? clean(fb.courier_name) : '');
    if (!groups.has(awb)) groups.set(awb, { awb, courier: '', items: [] });
    const g = groups.get(awb);
    if (!g.courier && courier) g.courier = courier;
    g.items.push(it);
  });
  const list = [...groups.values()];
  if (!list.length) list.push({ awb: fbAwb, courier: clean(fb.courier_name), items: [] });
  return list.sort((a, b) => (b.awb ? 1 : 0) - (a.awb ? 1 : 0));
}

// Item list that shows as many whole items as fit on the label, then "+ N more item(s)"
function Contents({ items }) {
  const ref = useRef(null);
  const [rows, setRows] = useState(items.length);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && rows > 1 && el.scrollHeight > el.clientHeight + 1) setRows((r) => r - 1);
  }, [rows]);
  const hidden = items.length - rows;
  const compact = items.length > 2; // one line per item when there are several
  return (
    <div ref={ref} className='border-b-2 border-black px-3 py-2 flex-1 min-h-0 overflow-hidden text-[10px]' data-testid='label-contents'>
      <p className={cap}>Contents</p>
      {items.slice(0, rows).map((it, i) => {
        const sub = [it.variant, it.sku].filter(Boolean).join(' · ');
        const qty = <span className='font-bold whitespace-nowrap shrink-0 w-8 text-right'>x {it.quantity || 1}</span>;
        return compact ? (
          <div key={i} className='flex items-baseline gap-2 py-px' data-testid='label-item'>
            <span className='font-bold truncate min-w-0 flex-1'>{it.product_name || it.name}</span>
            {sub && <span className='text-[9px] whitespace-nowrap shrink-0'>{sub}</span>}
            {qty}
          </div>
        ) : (
          <div key={i} className='flex items-start gap-2 py-0.5' data-testid='label-item'>
            <div className='min-w-0 flex-1'>
              <p className='font-bold truncate'>{it.product_name || it.name}</p>
              {sub && <p className='text-[9px] truncate'>{sub}</p>}
            </div>
            {qty}
          </div>
        );
      })}
      {hidden > 0 && <p className='py-0.5 italic' data-testid='label-more-items'>+ {hidden} more item(s)</p>}
    </div>
  );
}

function Label({ order, seller, pkg, index, count, firstAwb }) {
  const show = (key) => seller?.[key] !== false;
  const isA4 = seller?.label_size === 'a4';
  const isCod = (order.payment_method || '').toLowerCase() === 'cod' && order.payment_status !== 'paid';
  const total = Number(order.total || 0);
  const items = pkg.items;
  const totalQty = items.reduce((a, i) => a + Number(i.quantity || 1), 0);
  const weightG = count === 1 ? (order.shipments || []).reduce((a, s) => a + Number(s.weight_g || 0), 0) : 0;
  const pincodes = String(order.delivery_address || '').match(/\b\d{6}\b/g) || [];
  const pincode = pincodes[pincodes.length - 1] || '';
  const sellerName = seller?.seller_name || seller?.store_name || 'IMPORTREND';
  const sellerAddress = [seller?.seller_address, seller?.seller_city, seller?.seller_state, seller?.seller_pincode].filter(Boolean).join(', ');
  const barMax = isA4 ? 520 : 340;
  const courier = show('label_show_courier') ? pkg.courier : '';
  const showAwb = Boolean(pkg.awb) && show('label_show_awb');
  const showAwbBarcode = Boolean(pkg.awb) && show('label_show_awb_barcode');
  const wantsAwb = show('label_show_awb') || show('label_show_awb_barcode');
  const showCod = show('label_show_cod_amount');
  const sellerLine = [show('label_show_seller_phone') && seller?.seller_phone ? `Ph: ${seller.seller_phone}` : '', seller?.seller_gstin ? `GSTIN: ${seller.seller_gstin}` : ''].filter(Boolean).join('   ');

  return (
    <div
      className='ship-label mx-auto bg-white text-black border-2 border-black shadow-lg overflow-hidden flex flex-col'
      style={isA4 ? { width: '150mm', height: '200mm' } : { width: '4in', height: '6in' }}
      data-testid='shipping-label'
    >
      {/* Courier (or your business name) + payment type */}
      <div className={`${box} flex items-center justify-between gap-2`}>
        <div className='min-w-0'>
          {courier ? (
            <>
              <p className={cap}>Courier</p>
              <p className='text-lg font-black leading-tight truncate' data-testid='label-courier'>{courier}</p>
            </>
          ) : (
            <p className='text-lg font-black leading-tight truncate'>{sellerName}</p>
          )}
          {count > 1 && <p className='text-[10px] font-bold mt-0.5' data-testid='label-package'>Package {index + 1} of {count}</p>}
        </div>
        <div className={`shrink-0 px-3 py-1.5 text-sm font-black tracking-widest ${isCod ? 'bg-black text-white' : 'border-2 border-black'}`} data-testid='label-payment-type'>
          {isCod ? 'COD' : 'PREPAID'}
        </div>
      </div>

      {/* Tracking (AWB) number and its barcode */}
      {(showAwb || showAwbBarcode) && (
        <div className={`${box} text-center`} data-testid='label-awb-block'>
          {showAwbBarcode && <Barcode value={pkg.awb} maxWidth={barMax} height={isA4 ? 70 : 52} testId='label-awb-barcode' />}
          {showAwb && <p className='text-sm font-black font-mono tracking-wider mt-1' data-testid='label-awb'>AWB: {pkg.awb}</p>}
        </div>
      )}
      {!pkg.awb && wantsAwb && (
        <div className='no-print border-b-2 border-dashed border-slate-300 px-3 py-2 text-[10px] text-slate-500 shrink-0' data-testid='label-awb-missing'>
          No tracking number yet. In the order&apos;s Items tab, mark the item Shipped and enter the courier and tracking number to print the AWB barcode.
        </div>
      )}

      {/* Ship to */}
      <div className={box}>
        <p className={cap}>Ship To</p>
        <p className='text-base font-black mt-0.5 leading-tight' data-testid='label-ship-to-name'>{order.customer_name}</p>
        <p className='text-xs mt-0.5 leading-snug'>{order.delivery_address}</p>
        <div className='flex items-end justify-between mt-1'>
          {order.customer_phone ? <p className='text-sm font-bold'>Ph: {order.customer_phone}</p> : <span />}
          {pincode && <p className='text-2xl font-black tracking-wider leading-none' data-testid='label-pincode'>{pincode}</p>}
        </div>
      </div>

      {/* Amount to collect + shipment details */}
      <div className={`${box} flex items-stretch gap-3`}>
        {showCod && (
          <div className='flex-1 min-w-0 text-center border-r-2 border-black pr-3 flex flex-col justify-center' data-testid='label-collect'>
            {!isCod ? (
              <p className='text-sm font-black tracking-wide leading-tight'>PREPAID<br />DO NOT COLLECT CASH</p>
            ) : index === 0 ? (
              <>
                <p className={cap}>Collect cash on delivery</p>
                <p className='text-2xl font-black leading-tight'>Rs. {money(total)}</p>
              </>
            ) : (
              <p className='text-xs font-black leading-tight'>COD amount is collected with package 1{firstAwb ? ` (AWB ${firstAwb})` : ''}</p>
            )}
          </div>
        )}
        <div className={`text-[10px] leading-snug ${showCod ? 'w-[118px] shrink-0' : 'flex-1 grid grid-cols-3 gap-2'}`}>
          <p><span className={cap}>Items </span><b>{totalQty}</b></p>
          <p><span className={cap}>Weight </span><b>{weightG > 0 ? `${(weightG / 1000).toFixed(2)} kg` : '-'}</b></p>
          <p><span className={cap}>Value </span><b>Rs. {money(total)}</b></p>
        </div>
      </div>

      {/* Items */}
      {show('label_show_items') && items.length > 0 ? <Contents items={items} /> : <div className='flex-1' />}

      {/* Order number and its barcode */}
      <div className={`${box} text-center`} data-testid='label-order'>
        {show('label_show_order_barcode') && <Barcode value={order.order_number} maxWidth={barMax} height={isA4 ? 44 : 28} testId='label-order-barcode' />}
        <p className='text-[10px] font-bold font-mono mt-0.5'>Order {order.order_number} · {fmtDate(order.created_at)}</p>
      </div>

      {/* Return address */}
      {show('label_show_return_address') && (
        <div className='px-3 py-2 shrink-0' data-testid='label-return-address'>
          <p className={cap}>From / Return To</p>
          <p className='text-xs font-black'>{sellerName}</p>
          {sellerAddress
            ? <p className='text-[10px] leading-snug'>{sellerAddress}</p>
            : <p className='no-print text-[10px] italic text-slate-500'>Add your address in Settings &gt; Invoice &amp; Business Details</p>}
          {sellerLine && <p className='text-[10px] font-mono whitespace-pre'>{sellerLine}</p>}
        </div>
      )}
    </div>
  );
}

// Shipping label(s) for one order. Black and white only, so they print cleanly on thermal printers.
// The page sits in its own layer directly in <body>, so printing shows only the labels, one per page.
export default function ShippingLabel() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const token = localStorage.getItem('it_token');

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    const load = async () => {
      try {
        const sRes = await axios.get(`${API}/settings`);
        setSeller(sRes.data);
        let found = null;
        try {
          found = (await axios.get(`${API}/admin/orders/${orderId}`, { headers })).data;
        } catch {
          const list = (await axios.get(`${API}/admin/orders?limit=1000`, { headers })).data;
          found = list.find((o) => o.id === orderId || o.order_number === orderId) || null;
        }
        if (!found) { setError('Order not found'); return; }
        setOrder(found);
      } catch (e) {
        setError(e.response?.data?.detail || 'Failed to load order');
      } finally { setLoading(false); }
    };
    load();
  }, [orderId, token]);

  if (loading) return (
    <div className='flex items-center justify-center h-screen bg-slate-50'>
      <div className='w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin' />
    </div>
  );

  if (error) return (
    <div className='flex flex-col items-center justify-center h-screen bg-slate-50 gap-4'>
      <p className='text-red-500 font-semibold'>{error}</p>
      <Link to='/admin/orders' className='text-blue-600 text-sm hover:underline flex items-center gap-1'>
        <ArrowLeft className='w-4 h-4' /> Back to Orders
      </Link>
    </div>
  );

  const isA4 = seller?.label_size === 'a4';
  const pkgs = packagesOf(order);
  const firstAwb = pkgs[0]?.awb || '';

  return createPortal(
    <div className='label-portal'>
      <style>{`
        .label-portal { position: fixed; inset: 0; z-index: 60; overflow: auto; background: #f1f5f9; }
        @media print {
          body > *:not(.label-portal) { display: none !important; }
          html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
          .label-portal { position: static !important; overflow: visible !important; background: #fff !important; }
          .label-portal .no-print { display: none !important; }
          .label-portal .label-sheet { padding: 0 !important; margin: 0 !important; min-height: 0 !important; background: #fff !important; }
          .label-portal .ship-label { margin: 0 !important; box-shadow: none !important; break-after: page; page-break-after: always; }
          .label-portal .ship-label:last-child { break-after: auto; page-break-after: auto; }
        }
        @page { size: ${isA4 ? 'A4' : '4in 6in'}; margin: ${isA4 ? '10mm' : '0'}; }
      `}</style>

      <div className='no-print bg-slate-800 text-white px-6 py-3 flex items-center justify-between'>
        <div className='flex items-center gap-3'>
          <Link to='/admin/orders' className='text-slate-300 hover:text-white flex items-center gap-1.5 text-sm transition-colors'>
            <ArrowLeft className='w-4 h-4' /> Back to Orders
          </Link>
          <span className='text-slate-600'>|</span>
          <span className='text-sm font-semibold'>Shipping label · {order.order_number}</span>
        </div>
        <div className='flex items-center gap-2'>
          <Link to='/admin/settings' className='text-xs text-slate-300 hover:text-white border border-slate-600 rounded-lg px-3 py-1.5 transition-colors'>
            Label settings
          </Link>
          <Link to={`/admin/invoice/${order.id}`} className='flex items-center gap-1.5 text-xs text-slate-300 hover:text-white border border-slate-600 rounded-lg px-3 py-1.5 transition-colors'>
            <FileText className='w-3.5 h-3.5' /> Invoice
          </Link>
          <button
            onClick={() => window.print()}
            data-testid='label-print-btn'
            className='flex items-center gap-1.5 text-xs bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-4 py-1.5 font-bold transition-colors'
          >
            <Printer className='w-3.5 h-3.5' /> {pkgs.length > 1 ? `Print ${pkgs.length} Labels` : 'Print Label'}
          </button>
        </div>
      </div>

      <div className='label-sheet py-8 px-4 space-y-6'>
        {pkgs.map((pkg, i) => (
          <Label key={`${pkg.awb}-${i}`} order={order} seller={seller} pkg={pkg} index={i} count={pkgs.length} firstAwb={firstAwb} />
        ))}
      </div>
    </div>,
    document.body,
  );
}
